import React from 'react';
import { useParams } from 'react-router-dom';
import { ktpTypes } from '../../types/ktp';
import { Helmet } from 'react-helmet-async';
import RelatedCategories from '../../components/catalog/RelatedCategories';
import ContactForm from '../../components/forms/ContactForm';
import { ArrowRight, FileText, Truck, Briefcase, DollarSign, Factory, Shield } from 'lucide-react';

// НОВЫЙ КОМПОНЕНТ KtpDetailHero для детальных страниц КТП
interface KtpDetailHeroProps {
  ktpType: (typeof ktpTypes)[keyof typeof ktpTypes]; // Тип данных для конкретного КТП
}

const KtpDetailHero: React.FC<KtpDetailHeroProps> = ({ ktpType }) => {
  const advantages = [
    {
      icon: <Truck className="w-6 h-6 text-primary-500" />,
      text: 'Доставка от 3 дней по России и СНГ',
    },
    {
      icon: <Briefcase className="w-6 h-6 text-primary-500" />,
      text: 'Коммерческое предложение за 1 день — просто отправьте ТЗ или КП конкурентов',
    },
    {
      icon: <DollarSign className="w-6 h-6 text-primary-500" />,
      text: 'Цены ниже на 5–20%, чем в среднем по рынку',
    },
    {
      icon: <Factory className="w-6 h-6 text-primary-500" />,
      text: 'Собственное производство — 100% контроль качества',
    },
    {
      icon: <Shield className="w-6 h-6 text-primary-500" />,
      text: 'Гарантия на продукци от 3х лет, комплект сертификатов и паспартов',
    },
  ];

  return (
    <section className="relative overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0 min-h-[70vh]">
        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
          <source src="/videos/0605.mp4" type="video/mp4" /> {/* Видео для КТП */}
          Ваш браузер не поддерживает видео.
        </video>
        {/* Overlay */}
        <div className="absolute inset-0 bg-primary-900/70"></div> {/* Увеличена прозрачность */}
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 pt-16 pb-16 md:pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left column: New Content */}
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight text-white">
              Надёжные подстанции с гарантией результата — дешевле, быстрее.
              <span className="inline-flex items-center ml-4">
                Отправте запрос
                <ArrowRight className="ml-2 w-8 h-8 text-white animate-bounceX" />
              </span>
            </h1>
            <p className="text-primary-100 text-lg mb-4">{ktpType.title}</p>

            <div className="space-y-4 mb-8">
              {advantages.map((item, index) => (
                <div key={index} className="flex items-center text-white text-lg">
                  {item.icon}
                  <span className="ml-3">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center text-primary-100 animate-bounceX">
              <FileText className="w-6 h-6 mr-2" />
              <span className="font-medium">
                Есть КП конкурентов? Отправьте файл и получите лучшее предложение!
              </span>
            </div>
          </div>

          {/* Right column: Form */}
          <div>
            <div className="bg-white text-gray-900 rounded-xl shadow-xl overflow-hidden">
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Получить консультацию</h3>
                <p className="text-gray-600 mb-6">
                  Заполните форму, и наш менеджер свяжется с вами в течение 2 часов (в рабочее
                  время).
                </p>
                <ContactForm /> {/* Используем существующий компонент формы */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const KtpDetailPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const ktpType = type ? ktpTypes[type] : null;

  if (!ktpType) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-red-600">Тип подстанции не найден</h1>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{ktpType.title} | Болтнев</title>
        <meta name="description" content={ktpType.description} />
      </Helmet>

      {/* Добавляем новый Hero блок для детальной страницы КТП */}
      <KtpDetailHero ktpType={ktpType} />

      {/* Секция с оригинальным заголовком и описанием */}
      <div className="container-custom py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">{ktpType.title}</h1>
        <p className="text-xl text-gray-600 max-w-3xl mb-8">{ktpType.description}</p>
      </div>

      {/* Основные характеристики */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <img
                src={ktpType.image_url}
                alt={ktpType.title}
                className="w-full aspect-[3/2] object-contain rounded-lg shadow-lg"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4">Основные характеристики</h2>
              <ul className="space-y-2">
                {ktpType.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Технические характеристики */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Технические характеристики</h2>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <tbody>
              {ktpType.specifications.map((spec, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{spec.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{spec.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Преимущества */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Преимущества</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ktpType.advantages.map((advantage, index) => (
            <div key={index} className="bg-white p-4 rounded-lg shadow">
              <p className="text-gray-700">{advantage}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Применение */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Применение</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ktpType.applications.map((application, index) => (
            <div key={index} className="bg-white p-4 rounded-lg shadow text-center">
              <p className="text-gray-700">{application}</p>
            </div>
          ))}
        </div>
      </div>

      {/* УТП */}
      <div className="mb-12 bg-blue-50 p-6 rounded-lg">
        <h2 className="text-2xl font-bold mb-4">Почему выбирают нас</h2>
        <div className="space-y-4">
          {ktpType.utp.map((item, index) => (
            <div key={index} className="flex items-start">
              <svg
                className="w-6 h-6 text-blue-500 mr-2 mt-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Специальные предложения */}
      <div className="mb-12 bg-green-50 p-6 rounded-lg">
        <h2 className="text-2xl font-bold mb-4">Специальные предложения</h2>
        <div className="space-y-4">
          {ktpType.offers.map((offer, index) => (
            <div key={index} className="flex items-center">
              <svg
                className="w-6 h-6 text-green-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                />
              </svg>
              <p className="text-gray-700">{offer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Смежное оборудование */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Смежное оборудование</h2>
        <RelatedCategories currentCategory="ktp" />
      </div>

      {/* SEO текст */}
      <div className="mt-12 text-gray-600">
        <p>{ktpType.seo_text}</p>
      </div>
    </div>
  );
};

export default KtpDetailPage;
